import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  FaDownload,
  FaEllipsisV,
  FaPlus,
  FaShieldAlt,
  FaTrash,
  FaPen,
  FaChevronDown,
  FaTimes,
  FaArrowRight,
  FaEye,
  FaIdBadge,
  FaUserCog,
  FaUserPlus,
} from "react-icons/fa";
import "../styles/UserManagement.css";
import Pagination from "../components/Pagination";
import {
  createOrgUser,
  deleteOrgUser,
  listOrgUsers,
  updateOrgUser,
  listSystemUsers,
  createSystemUser,
  updateSystemUser,
  deleteSystemUser,
} from "../api/services/usersService";
import { listOrgUsersManaged, getOrgUsersAuditTimeline } from "../api/api";
import { getUserData } from "../utils/authStorage";
import { useToast } from "../context/ToastContext";
import useSidebarAction from "../hooks/useSidebarAction";

const extractUsers = (res) => {
  if (Array.isArray(res)) return res;
  const candidates = [
    res?.data,
    res?.users,
    res?.data?.users,
    res?.data?.data,
    res?.result,
    res?.data?.result,
    res?.orgUsers,
    res?.data?.orgUsers,
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
};

// Reads the total record count from any common pagination response shape.
const extractTotal = (res) => {
  const candidates = [
    res?.pagination?.total,
    res?.pagination?.totalRecords,
    res?.pagination?.totalCount,
    res?.total,
    res?.totalRecords,
    res?.totalCount,
    res?.count,
    res?.data?.total,
    res?.data?.totalRecords,
    res?.data?.pagination?.total,
    res?.meta?.total,
  ];
  for (const c of candidates) {
    if (typeof c === "number") return c;
  }
  return null;
};

const SORT_OPTIONS = [
  { label: "Last Activity", value: "last_activity" },
  { label: "Name", value: "name" },
];

// Role filter pills for the super-admin system-users list.
// Values must match the API's `role` query param exactly.
const SYSTEM_ROLE_FILTERS = [
  { label: "All Roles", value: "all" },
  { label: "Super Admin", value: "Super Admin" },
  { label: "Moderator", value: "Moderator" },
  { label: "Auditor", value: "Auditor" },
];

const DEPARTMENT_OPTIONS = [
  { label: "Operations", value: "Operations" },
  { label: "Engineering", value: "Engineering" },
  { label: "Finance", value: "Finance" },
];

const ROLE_OPTIONS = [
  { label: "Super Admin", value: "super_admin", icon: FaIdBadge },
  { label: "Moderator", value: "moderator", icon: FaUserCog },
  { label: "Auditor", value: "auditor", icon: FaEye },
];

const PERMISSION_SECTIONS = [
  {
    title: "Entity Management",
    accentClass: "um-drawer__groupTitle",
    items: [
      { key: "createOrganization", label: "Create Organization" },
      { key: "editOrganization", label: "Edit Organization" },
      { key: "blockOrganization", label: "Block Organization" },
    ],
  },
  {
    title: "Financial Reports",
    accentClass: "um-drawer__groupTitle",
    items: [
      { key: "downloadReports", label: "Download Reports" },
      { key: "viewAnalytics", label: "View Analytics" },
    ],
  },
  {
    title: "System Administration",
    accentClass: "um-drawer__groupTitle",
    items: [
      { key: "manageUsers", label: "Manage Users" },
    ],
  },
];

const DEFAULT_PERMISSIONS = {
  createOrganization: true,
  editOrganization: true,
  blockOrganization: false,
  downloadReports: true,
  viewAnalytics: true,
  manageUsers: false,
};

const ADD_USER_INITIAL_VALUES = {
  name: "",
  username: "",
  department: "Operations",
  roles: ["super_admin"],
  permissions: DEFAULT_PERMISSIONS,
};

const inferDialogRole = (user) => {
  const firstKnownRole = Array.isArray(user?.roles)
    ? user.roles.find((role) => ROLE_OPTIONS.some((option) => option.value === role))
    : null;

  if (firstKnownRole) return firstKnownRole;

  const type = String(user?.user_type || "").toLowerCase();
  if (type.includes("audit") || type.includes("compliance")) return "auditor";
  if (type.includes("admin")) return "super_admin";
  return "moderator";
};

const countActivePermissions = (permissions) => Object.values(permissions || {}).filter(Boolean).length;

// Maps API role label ("Super Admin") → form value ("super_admin").
const ROLE_LABEL_TO_VALUE = ROLE_OPTIONS.reduce((acc, r) => {
  acc[r.label] = r.value;
  return acc;
}, {});

const formatLastLogin = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Flattens a /users/allSystemUsers row into the shape the table/dialog/drawer use.
const normalizeSystemUser = (row = {}) => {
  const admin = row.administrator || {};
  const perms = row.permissions || {};
  const roleLabel = row.role || row.user_type || "";
  return {
    ...row,
    _id: row.id || row._id,
    name: admin.name || row.name || "",
    email: admin.email || row.email || "",
    username: admin.email || row.username || "",
    avatarText: admin.avatarText,
    isActive: admin.isActive,
    user_type: roleLabel,
    role: roleLabel,
    roles: [ROLE_LABEL_TO_VALUE[roleLabel] || "moderator"],
    permissions: perms.list || perms.permissions || {},
    permissions_active: perms.active,
    permissions_total: perms.total,
    last_login: formatLastLogin(row.lastLogin),
    last_activity: row.lastLogin || "",
    session_status: row.sessionStatus || "",
    isDisabled: Boolean(row.isDisabled),
  };
};

const addUserValidationSchema = Yup.object().shape({
  name: Yup.string()
    .matches(/^[A-Za-z\s]+$/, "Only letters and spaces allowed")
    .required("Full name is required"),
  username: Yup.string()
    .email("Enter a valid email address")
    .required("Enterprise email is required"),
  department: Yup.string().required("Department is required"),
  roles: Yup.array()
    .of(Yup.string().oneOf(ROLE_OPTIONS.map((role) => role.value), "Invalid role"))
    .min(1, "Select an initial role")
    .required("Initial role is required"),
});

const PAGE_SIZE = 4;

const roleTag = (value = "") => {
  const raw = String(value).toLowerCase();
  if (raw.includes("admin")) return "Admin";
  if (raw.includes("support")) return "Support";
  if (raw.includes("analyst") || raw.includes("manager")) return "Manager";
  return "Manager";
};

const statusTag = (user) => {
  /* Prefer the explicit status field from the API (e.g. "ACTIVE", "INACTIVE", "PENDING") */
  const explicit = String(user?.status || "").toLowerCase();
  if (explicit.includes("inactive") || explicit.includes("disable") || explicit.includes("block") || explicit.includes("revoked")) return "Inactive";
  if (explicit.includes("pending") || explicit.includes("invite")) return "Pending";
  if (explicit.includes("active")) return "Active";
  if (user?.isDisabled === true) return "Inactive";

  /* Fallback: infer from session info (legacy rows without a status field) */
  const raw = String(user?.session_status || user?.last_ip || "").toLowerCase();
  if (raw.includes("active")) return "Active";
  if (raw.includes("offline") || raw.includes("revoked")) return "Inactive";
  return "Pending";
};

const UsersList = ({ isSidebarOpen }) => {
  const [loading, setLoading] = useState(true);
  const [users, setAllUsers] = useState([]);
  const [serverTotal, setServerTotal] = useState(null);
  const [auditItems, setAuditItems] = useState([]);
  const [showAllAudit, setShowAllAudit] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState("last_activity");
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerUser, setDrawerUser] = useState(null);
  const [drawerPermissions, setDrawerPermissions] = useState(DEFAULT_PERMISSIONS);
  const [drawerSaving, setDrawerSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDialogMode, setAddDialogMode] = useState("create");
  const [addDialogUser, setAddDialogUser] = useState(null);
  const [addDialogStep, setAddDialogStep] = useState(1);
  const [departmentMenuOpen, setDepartmentMenuOpen] = useState(false);
  const [actionMenuFor, setActionMenuFor] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const { showSuccess, showError } = useToast();
  const sortMenuRef = React.useRef(null);
  const departmentMenuRef = React.useRef(null);

  const userData = getUserData();
  const user = userData?.user || userData?.data || userData || {};
  const orgId = user?.profile?.orgId || user?.orgId || user?.organizationId || user?.orgID;
  const userType = String(user?.user_type || user?.type || "").toLowerCase();
  const isOrgAdmin = userType === "org" || userType === "org-admin" || userType === "organizationadmin";
  const isSuperAdmin = userType === "admin";

  // Debounce the search box so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to first page whenever a filter/search/sort changes.
  useEffect(() => {
    setPage(0);
  }, [activeFilter, sortBy, debouncedSearch]);

  // Non-super-admin users.
  //  • Org admin → server-side /users/org-users/list (+ audit timeline)
  //  • Subordinate → legacy /users/allOrgUsers (client-side)
  useEffect(() => {
    if (isSuperAdmin) return;
    let active = true;
    const fetchNonSystem = async () => {
      if (!orgId) {
        setAllUsers([]);
        setServerTotal(isOrgAdmin ? 0 : null);
        setLoading(false);
        return;
      }
      try {
        if (isOrgAdmin) {
          const [listRes, auditRes] = await Promise.all([
            listOrgUsersManaged({
              orgId,
              page: page + 1,
              limit: PAGE_SIZE,
              role: activeFilter === "all" ? "all" : activeFilter,
              status: "all",
              sortBy: sortBy === "name" ? "name" : "newest",
              search: debouncedSearch,
            }),
            getOrgUsersAuditTimeline(orgId, 10),
          ]);
          if (!active) return;
          setAllUsers(extractUsers(listRes));
          setServerTotal(extractTotal(listRes));
          setAuditItems(extractUsers(auditRes));
        } else {
          const res = await listOrgUsers(orgId);
          if (!active) return;
          setAllUsers(extractUsers(res));
          setServerTotal(null);
        }
      } catch (err) {
        if (!active) return;
        console.error("Error fetching users:", err);
        setAllUsers([]);
        setServerTotal(isOrgAdmin ? 0 : null);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchNonSystem();
    return () => {
      active = false;
    };
  }, [isSuperAdmin, isOrgAdmin, orgId, page, activeFilter, debouncedSearch, sortBy]);

  // Super-admin system users → server-side (refetch on page/filter/search/sort).
  useEffect(() => {
    if (!isSuperAdmin) return;
    let active = true;
    const fetchSystemUsers = async () => {
      try {
        const res = await listSystemUsers({
          page: page + 1,
          limit: PAGE_SIZE,
          role: activeFilter,
          search: debouncedSearch,
          sortBy: sortBy === "name" ? "name" : "",
        });
        if (!active) return;
        setAllUsers(extractUsers(res).map(normalizeSystemUser));
        setServerTotal(extractTotal(res));
      } catch (err) {
        if (!active) return;
        console.error("Error fetching system users:", err);
        setAllUsers([]);
        setServerTotal(0);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchSystemUsers();
    return () => {
      active = false;
    };
  }, [isSuperAdmin, page, activeFilter, debouncedSearch, sortBy]);

  useEffect(() => {
    const handleDocMouseDown = (e) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(e.target)) {
        setSortMenuOpen(false);
      }
      if (departmentMenuRef.current && !departmentMenuRef.current.contains(e.target)) {
        setDepartmentMenuOpen(false);
      }
      if (!e.target.closest(".um-actionswrap")) {
        setActionMenuFor(null);
      }
    };

    document.addEventListener("mousedown", handleDocMouseDown);
    return () => document.removeEventListener("mousedown", handleDocMouseDown);
  }, []);

  useEffect(() => {
    if (!drawerOpen && !addDialogOpen && !deleteTarget) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setDrawerOpen(false);
        setAddDialogOpen(false);
        setDeleteTarget(null);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [drawerOpen, addDialogOpen, deleteTarget]);

  const openDrawer = (u) => {
    setDrawerUser(u);
    setDrawerPermissions({ ...DEFAULT_PERMISSIONS, ...(u?.permissions || {}) });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const openDeleteDialog = (userRecord) => {
    setDrawerOpen(false);
    setDeleteTarget(userRecord);
  };

  const closeDeleteDialog = () => {
    setDeleteTarget(null);
  };

  const openAddDialog = () => {
    setDrawerOpen(false);
    setAddDialogMode("create");
    setAddDialogUser(null);
    setAddDialogStep(1);
    setAddDialogOpen(true);
  };

  /* Sidebar dropdown action → open the same create-user dialog */
  useSidebarAction({ create: openAddDialog });

  const openEditDialog = (userRecord) => {
    setDrawerOpen(false);
    setAddDialogMode("edit");
    setAddDialogUser(userRecord);
    setAddDialogStep(1);
    setAddDialogOpen(true);
  };

  const closeAddDialog = () => {
    setAddDialogOpen(false);
    setAddDialogMode("create");
    setAddDialogUser(null);
    setAddDialogStep(1);
    setDepartmentMenuOpen(false);
  };

  const refreshUsers = async () => {
    if (isSuperAdmin) {
      const res = await listSystemUsers({
        page: page + 1,
        limit: PAGE_SIZE,
        role: activeFilter,
        search: debouncedSearch,
        sortBy: sortBy === "name" ? "name" : "",
      });
      setAllUsers(extractUsers(res).map(normalizeSystemUser));
      setServerTotal(extractTotal(res));
      return;
    }
    if (isOrgAdmin) {
      const [listRes, auditRes] = await Promise.all([
        listOrgUsersManaged({
          orgId,
          page: page + 1,
          limit: PAGE_SIZE,
          role: activeFilter === "all" ? "all" : activeFilter,
          status: "all",
          sortBy: sortBy === "name" ? "name" : "newest",
          search: debouncedSearch,
        }),
        getOrgUsersAuditTimeline(orgId, 10),
      ]);
      setAllUsers(extractUsers(listRes));
      setServerTotal(extractTotal(listRes));
      setAuditItems(extractUsers(auditRes));
      return;
    }
    const res = await listOrgUsers(orgId);
    setAllUsers(extractUsers(res));
  };

  const handleSaveUser = async (values, { resetForm }) => {
    setCreateLoading(true);

    try {
      const roleLabel = ROLE_OPTIONS.find((role) => role.value === values.roles[0])?.label || "Super Admin";
      const activePermissionCount = countActivePermissions(values.permissions);

      /* ── Super-admin system users (real API, orgId = null) ── */
      if (isSuperAdmin) {
        if (addDialogMode === "edit" && addDialogUser) {
          const updatePayload = {
            name: values.name.trim(),
            department: values.department,
            initialRole: roleLabel,
            permissions: values.permissions,
          };
          const res = await updateSystemUser(addDialogUser._id, updatePayload);
          if (res?.isSuccess || res?.data || res?.message) {
            await refreshUsers();
            showSuccess("Updated", res?.message || "User updated successfully");
            resetForm();
            closeAddDialog();
          } else {
            showError("Error", res?.message || "Failed to update user", 3000);
          }
          return;
        }

        const createPayload = {
          name: values.name.trim(),
          email: values.username.trim(),
          department: values.department,
          initialRole: roleLabel,
          orgId: null,
          permissions: values.permissions,
        };
        const res = await createSystemUser(createPayload);
        if (res?.isSuccess || res?.data || res?.message) {
          await refreshUsers();
          showSuccess("Created", res?.message || "User created successfully");
          resetForm();
          closeAddDialog();
        } else {
          showError("Error", res?.message || "Failed to create user", 3000);
        }
        return;
      }

      if (addDialogMode === "edit" && addDialogUser) {
        const updatedPayload = {
          name: values.name.trim(),
          department: values.department,
          initialRole: roleLabel,
          permissions: values.permissions,
        };

        if (!orgId) {
          setAllUsers((prev) => prev.map((item) => (
            item._id === addDialogUser._id
              ? {
                  ...item,
                  name: values.name.trim(),
                  department: values.department,
                  roles: values.roles,
                  permissions: values.permissions,
                  permissions_active: activePermissionCount,
                  permissions_total: Object.keys(DEFAULT_PERMISSIONS).length,
                  user_type: roleLabel,
                }
              : item
          )));
          showSuccess("Updated", "User updated successfully");
          resetForm();
          closeAddDialog();
          return;
        }

        const updateRes = await updateOrgUser(addDialogUser._id, updatedPayload);
        if (updateRes?.isSuccess || updateRes?.data || updateRes?.message) {
          await refreshUsers();
          showSuccess("Updated", updateRes?.message || "User updated successfully");
          resetForm();
          closeAddDialog();
        } else {
          showError("Error", updateRes?.message || "Failed to update user", 3000);
        }
        return;
      }

      const payload = {
        name: values.name.trim(),
        email: values.username.trim(),
        department: values.department,
        initialRole: roleLabel,
        orgId: orgId ?? null,
        permissions: values.permissions,
      };

      if (!orgId) {
        const demoUser = {
          _id: `demo-${Date.now()}`,
          name: values.name.trim(),
          username: values.username.trim(),
          email: values.username.trim(),
          user_type: roleLabel,
          roles: values.roles,
          permissions: values.permissions,
          permissions_active: activePermissionCount,
          permissions_total: Object.keys(DEFAULT_PERMISSIONS).length,
          last_login: "Just now",
          last_ip: "",
          session_status: "Active Session",
          last_activity: "Just now",
        };

        setAllUsers((prev) => [demoUser, ...prev]);
        showSuccess("Created", "User created successfully");
        resetForm();
        closeAddDialog();
        return;
      }

      const res = await createOrgUser(payload);
      if (res?.isSuccess || res?.data || res?.message) {
        await refreshUsers();
        showSuccess("Created", res?.message || "User created successfully");
        resetForm();
        closeAddDialog();
      } else {
        showError("Error", res?.message || "Failed to create user", 3000);
      }
    } catch (err) {
      console.error("Error creating user:", err);
      showError("Error", "Something went wrong while creating the user", 3000);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const res = isSuperAdmin
        ? await deleteSystemUser(userId)
        : await deleteOrgUser(userId);
      if (res?.isSuccess || res?.data || res?.message) {
        showSuccess("Deleted", res?.message || "User deleted successfully");
        refreshUsers();
      } else {
        showError("Error", res?.message || "Failed to delete user", 3000);
      }
    } catch {
      showError("Error", "Something went wrong", 3000);
    }
  };

  // Enable / disable a system user  →  PATCH /users/:id { isDisabled }
  const handleToggleDisable = async (userRecord) => {
    if (!userRecord?._id) return;
    const next = !userRecord.isDisabled;
    try {
      const res = await updateSystemUser(userRecord._id, { isDisabled: next });
      if (res?.isSuccess || res?.data || res?.message) {
        showSuccess(
          next ? "Disabled" : "Enabled",
          res?.message || `User ${next ? "disabled" : "enabled"} successfully`
        );
        await refreshUsers();
        closeDrawer();
      } else {
        showError("Error", res?.message || "Failed to update user", 3000);
      }
    } catch {
      showError("Error", "Something went wrong", 3000);
    }
  };

  // Save the drawer permission toggles  →  PATCH /users/:id { permissions }
  const handleSaveDrawerPermissions = async () => {
    const targetId = drawerUser?._id ?? drawerUser?.id;
    if (!targetId) {
      closeDrawer();
      return;
    }
    setDrawerSaving(true);
    try {
      const payload = { permissions: drawerPermissions };
      const res = isSuperAdmin
        ? await updateSystemUser(targetId, payload)
        : await updateOrgUser(targetId, payload);
      if (res?.isSuccess || res?.data || res?.message) {
        showSuccess("Saved", res?.message || "Permissions updated successfully");
        await refreshUsers();
        closeDrawer();
      } else {
        showError("Error", res?.message || "Failed to update permissions", 3000);
      }
    } catch {
      showError("Error", "Something went wrong", 3000);
    } finally {
      setDrawerSaving(false);
    }
  };

  const userCellTemplate = (rowData) => {
    const name = rowData?.name || rowData?.username || "—";
    const email = rowData?.email || rowData?.username || "";
    return (
      <div className="um-usercell">
        <div className="um-avatar">
          {String(name).slice(0, 1).toUpperCase()}
          <span className="um-online" />
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="um-name">{name}</div>
          <div className="um-email">{email}</div>
        </div>
      </div>
    );
  };

  const roleBadgeTemplate = (rowData) => {
    if (!isOrgAdmin) {
      const type = String(rowData?.user_type || "").toLowerCase();
      const label = rowData?.user_type || "—";
      const cls =
        type.includes("security")
          ? "um-badge um-badge--cyan"
          : type.includes("compliance")
          ? "um-badge um-badge--amber"
          : "um-badge";
      return <span className={cls}>{label}</span>;
    }

    const label = roleTag(rowData?.user_type || "");
    const cls =
      label === "Admin"
        ? "um-badge um-badge--admin"
        : label === "Support"
        ? "um-badge um-badge--support"
        : "um-badge um-badge--manager";
    return <span className={cls}>{label}</span>;
  };

  const permissionsTemplate = (rowData) => {
    if (!isOrgAdmin) {
      const active = Number(rowData?.permissions_active ?? rowData?.active_permissions ?? 0);
      const total = Number(rowData?.permissions_total ?? 12);
      const pct = total ? Math.min(100, Math.max(0, (active / total) * 100)) : 0;

      return (
        <div className="um-perms">
          <div className="um-perms__meta">
            <span className="um-perms__count">{active}/{total} active</span>
          </div>
          <div className="um-perms__bar">
            <div className="um-perms__fill" style={{ width: `${pct}%` }} />
          </div>
        </div>
      );
    }

    const items = Array.isArray(rowData?.roles) && rowData.roles.length
      ? rowData.roles.slice(0, 3)
      : ["merchant", "user", "audit"];

    const labels = items.map((item) =>
      String(item)
        .replace(/[_-]/g, " ")
        .replace(/\b\w/g, (ch) => ch.toUpperCase())
    );

    return (
      <div className="um-permchips">
        {labels.map((label) => (
          <span key={label} className="um-permchip">{label}</span>
        ))}
      </div>
    );
  };

  const statusTemplate = (rowData) => {
    if (!isOrgAdmin) {
      const t = rowData?.last_login || rowData?.last_activity || "—";
      const sub = rowData?.last_ip || "";
      const status = rowData?.session_status || "";
      return (
        <div className="um-lastlogin">
          <div className="um-lastlogin__top">{t}</div>
          {sub ? <div className="um-lastlogin__sub">{sub}</div> : null}
          {status ? <div className="um-lastlogin__status">{status}</div> : null}
        </div>
      );
    }

    const label = statusTag(rowData);
    const cls =
      label === "Active"
        ? "um-status um-status--active"
        : label === "Pending"
        ? "um-status um-status--pending"
        : "um-status um-status--inactive";

    return (
      <span className={cls}>{label}</span>
    );
  };

  /* ── Action buttons ── */
  const actionBodyTemplate = (rowData) => {
    if (isOrgAdmin && String(rowData.user_type || "").toLowerCase() === "admin") return null;

    if (!isOrgAdmin) {
      return (
        <div className="um-actions">
          <button
            className="um-iconbtn"
            title="Permissions & access"
            type="button"
            onClick={() => openDrawer(rowData)}
          >
            <FaShieldAlt />
          </button>
          <button
            className="um-iconbtn"
            title="Edit"
            type="button"
            onClick={() => openEditDialog(rowData)}
          >
            <FaPen />
          </button>
          <button
            className="um-iconbtn"
            title="Delete User"
            type="button"
            onClick={() => openDeleteDialog(rowData)}
          >
            <FaTrash />
          </button>
        </div>
      );
    }

    const rowKey = rowData._id ?? rowData.id ?? rowData.email ?? rowData.username;
    const menuOpen = actionMenuFor != null && actionMenuFor.id === rowKey;
    return (
      <div className="um-actions um-actionswrap">
        <button
          className="um-iconbtn"
          title="User actions"
          type="button"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={(e) => {
            if (menuOpen) {
              setActionMenuFor(null);
              return;
            }
            const rect = e.currentTarget.getBoundingClientRect();
            setActionMenuFor({
              id: rowKey,
              top: rect.bottom + 6,
              right: window.innerWidth - rect.right,
              row: rowData,
            });
          }}
        >
          <FaEllipsisV />
        </button>
      </div>
    );
  };

  /* Row actions menu — portaled to <body> so the table's overflow doesn't clip it */
  const actionMenuPortal = actionMenuFor
    ? createPortal(
        <div
          className="um-actionswrap"
          style={{ position: "fixed", top: actionMenuFor.top, right: actionMenuFor.right, zIndex: 3000 }}
        >
          <div className="um-sortmenu um-actionmenu" style={{ position: "static" }} role="menu">
            <button
              type="button"
              role="menuitem"
              className="um-sortmenu__item"
              onClick={() => {
                openEditDialog(actionMenuFor.row);
                setActionMenuFor(null);
              }}
            >
              <FaPen /> Edit User
            </button>
            <button
              type="button"
              role="menuitem"
              className="um-sortmenu__item"
              onClick={() => {
                openDrawer(actionMenuFor.row);
                setActionMenuFor(null);
              }}
            >
              <FaShieldAlt /> View Permissions
            </button>
            <button
              type="button"
              role="menuitem"
              className="um-sortmenu__item um-actionmenu__item--danger"
              onClick={() => {
                openDeleteDialog(actionMenuFor.row);
                setActionMenuFor(null);
              }}
            >
              <FaTrash /> Delete User
            </button>
          </div>
        </div>,
        document.body
      )
    : null;

  if (loading) {
    return (
      <div className={`msd-container ${isSidebarOpen ? "shifted" : ""}`}>
        <p className="msd-muted">Loading users…</p>
      </div>
    );
  }

  const filteredUsers = users.filter((u) => {
    if (activeFilter === "all") return true;
    const type = String(u?.user_type || "").toLowerCase();
    if (activeFilter === "security") return type.includes("security");
    if (activeFilter === "analyst") return type.includes("analyst");
    if (activeFilter === "compliance") return type.includes("compliance");
    return true;
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (!isOrgAdmin && sortBy === "name") {
      const an = String(a?.name || a?.username || "").toLowerCase();
      const bn = String(b?.name || b?.username || "").toLowerCase();
      return an.localeCompare(bn);
    }
    const al = String(a?.last_activity || "").toLowerCase();
    const bl = String(b?.last_activity || "").toLowerCase();
    return bl.localeCompare(al);
  });

  // Super-admin list is server-driven; org list stays client-side.
  // If the server ignores pagination and returns everything, slice locally as a fallback.
  const isServerMode = isSuperAdmin || isOrgAdmin;
  const total = isServerMode ? (serverTotal ?? users.length) : sortedUsers.length;
  const pageUsers = isServerMode
    ? (users.length > PAGE_SIZE
        ? users.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)
        : users)
    : sortedUsers.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const sortLabel = SORT_OPTIONS.find((o) => o.value === sortBy)?.label || "Last Activity";

  const adminsCount = sortedUsers.filter((u) => roleTag(u?.user_type) === "Admin").length;
  const managersCount = sortedUsers.filter((u) => roleTag(u?.user_type) === "Manager").length;
  const supportCount = sortedUsers.filter((u) => roleTag(u?.user_type) === "Support").length;
  const inactiveCount = sortedUsers.filter((u) => statusTag(u) === "Inactive").length;


  /* Export the full users table as an .xlsx file */
  const handleExportUsers = () => {
    const list = isServerMode ? users : sortedUsers;
    if (!list.length) {
      showError("Export", "No users to export", 3000);
      return;
    }
    const rows = list.map((u) => ({
      Name: u.name || u.username || "",
      Email: u.email || u.username || "",
      Role: u.user_type || u.role || "",
      Status: statusTag(u),
      "Active Permissions": u.permissions_active ?? "",
      "Last Login": u.last_login || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Users");
    XLSX.writeFile(wb, `users-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const TIMELINE_TONES = ["blue", "teal", "rose", "violet"];
  const auditTimeline = auditItems.map((it, idx) => {
    const rawAction = it.action ?? it.event ?? it.type ?? "";
    return {
      when: formatLastLogin(it.timestamp ?? it.createdAt ?? it.time ?? it.date ?? it.when) ,
      text:
        it.text ??
        it.message ??
        it.description ??
        it.detail ??
        (it.actorName ? `${rawAction || "Activity"} by ${it.actorName}` : rawAction) ??
        "Activity",
      action: rawAction ? `ACTION: ${String(rawAction).toUpperCase().replace(/\s+/g, "_")}` : "",
      tone: TIMELINE_TONES[idx % TIMELINE_TONES.length],
    };
  });

  /* Audit timeline: show the first 4, rest behind "Show more" */
  const AUDIT_PREVIEW_COUNT = 4;
  const visibleAuditTimeline = showAllAudit
    ? auditTimeline
    : auditTimeline.slice(0, AUDIT_PREVIEW_COUNT);
  const hasMoreAudit = auditTimeline.length > AUDIT_PREVIEW_COUNT;

  const drawerName = drawerUser?.name || drawerUser?.username || "—";
  const drawerEmail = drawerUser?.email || drawerUser?.username || "";
  const drawerRole = drawerUser?.user_type || "";
  const dialogInitialValues = addDialogMode === "edit" && addDialogUser
    ? {
        name: addDialogUser?.name || "",
        username: addDialogUser?.email || addDialogUser?.username || "",
        department: addDialogUser?.department || "Operations",
        roles: [inferDialogRole(addDialogUser)],
        permissions: {
          ...DEFAULT_PERMISSIONS,
          ...(addDialogUser?.permissions || {}),
        },
      }
    : ADD_USER_INITIAL_VALUES;

  return (
    <div className={`msd-container ${isSidebarOpen ? "shifted" : ""} um-page${isOrgAdmin ? " um-v2" : ""}`}>
      {isOrgAdmin ? (
        <>
          <div className="um-v2__topbar">
            <div className="um-v2__crumbs">Organization &nbsp;›&nbsp; User Management</div>
            <div className="um-v2__actions">
              <button type="button" className="um-v2__btn um-v2__btn--ghost" onClick={handleExportUsers}>
                <FaDownload /> Export
              </button>
              <button className="um-v2__btn um-v2__btn--primary" type="button" onClick={openAddDialog}>
                <FaUserPlus /> Create User
              </button>
            </div>
          </div>

          <div className="um-header">
            <div>
              <h2 className="um-title">User Management</h2>
              <div className="um-subtitle">
                Control access and permission levels across your organization.
              </div>
            </div>
          </div>

          <div className="um-v2__stats">
            <article className="um-v2__stat um-v2__stat--admins">
              <span>Admins</span>
              <strong>{adminsCount}</strong>
              <small>Full system access</small>
            </article>
            <article className="um-v2__stat um-v2__stat--managers">
              <span>Managers</span>
              <strong>{managersCount}</strong>
              <small>Team & resource management</small>
            </article>
            <article className="um-v2__stat um-v2__stat--support">
              <span>Support</span>
              <strong>{supportCount}</strong>
              <small>Ticket & client resolution</small>
            </article>
            <article className="um-v2__stat um-v2__stat--inactive">
              <span>Inactive</span>
              <strong>{inactiveCount}</strong>
              <small>Access revoked/pending</small>
            </article>
          </div>

          <div className="um-v2__grid">
            <div className={drawerOpen ? "um-tablewrap um-tablewrap--draweropen" : "um-tablewrap"}>
              <DataTable
                value={pageUsers}
                dataKey="_id"
                className="um-datatable"
                emptyMessage="No users found."
                rowHover
              >
                <Column header="User" body={userCellTemplate} style={{ width: "34%" }} />
                <Column header="Role" body={roleBadgeTemplate} style={{ width: "16%" }} sortable sortField="user_type" />
                <Column header="Permissions" body={permissionsTemplate} style={{ width: "25%" }} />
                <Column header="Status" body={statusTemplate} style={{ width: "15%" }} />
                <Column header="Actions" body={actionBodyTemplate} style={{ width: "10%" }} />
              </DataTable>

              <Pagination
                page={page}
                pageSize={PAGE_SIZE}
                total={total}
                onPageChange={setPage}
                itemLabel="users"
              />
            </div>

            <aside className="um-v2__aside">
              <article className="um-v2__panel">
                <div className="um-v2__panelhead">
                  <h3>Audit Timeline</h3>
                </div>
                <div className="um-v2__timeline">
                  {auditTimeline.length === 0 ? (
                    <div className="msd-muted" style={{ padding: 8, fontSize: 13 }}>No recent activity.</div>
                  ) : visibleAuditTimeline.map((item) => (
                    <div key={`${item.when}-${item.action}`} className="um-v2__timelineitem">
                      <span className={`um-v2__dot um-v2__dot--${item.tone}`} />
                      <div>
                        <small>{item.when}</small>
                        <p>{item.text}</p>
                        <em>{item.action}</em>
                      </div>
                    </div>
                  ))}
                </div>

                {hasMoreAudit && (
                  <button
                    type="button"
                    className="um-v2__timelinemore"
                    onClick={() => setShowAllAudit((v) => !v)}
                  >
                    {showAllAudit
                      ? "Show less"
                      : `Show more (${auditTimeline.length - AUDIT_PREVIEW_COUNT})`}
                  </button>
                )}
              </article>

              
            </aside>
          </div>
        </>
      ) : (
        <>
          <div className="um-header">
            <div>
              <h2 className="um-title">User Management</h2>
              <div className="um-subtitle">
                Manage access protocols and security credentials for {total} active administrators.
              </div>
            </div>
            <button className="um-addbtn" type="button" onClick={openAddDialog}>
              <FaPlus />
              Create User
            </button>
          </div>

          <div className="um-filtersbar">
            <div className="um-filtersleft">
              <div className="um-filterslabel">FILTERS:</div>
              <div className="um-pillrow">
                {SYSTEM_ROLE_FILTERS.map((f) => (
                  <button
                    key={f.value}
                    className={activeFilter === f.value ? "um-pill active" : "um-pill"}
                    type="button"
                    onClick={() => setActiveFilter(f.value)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="um-filtersright">
              <input
                type="text"
                className="um-searchinput"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search users…"
                style={{
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1px solid rgba(148,163,184,0.35)",
                  background: "transparent",
                  color: "inherit",
                  minWidth: 200,
                  outline: "none",
                }}
              />
              <div className="um-sortwrap" ref={sortMenuRef}>
                <button
                  type="button"
                  className={sortMenuOpen ? "um-sortpill open" : "um-sortpill"}
                  onClick={() => setSortMenuOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={sortMenuOpen}
                >
                  <span className="um-sortpill__label">SORT BY:</span>
                  <span className="um-sortpill__value">{sortLabel}</span>
                  <FaChevronDown className={sortMenuOpen ? "um-sortpill__icon open" : "um-sortpill__icon"} />
                </button>

                {sortMenuOpen ? (
                  <div className="um-sortmenu" role="menu">
                    {SORT_OPTIONS.map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        role="menuitem"
                        className={o.value === sortBy ? "um-sortmenu__item active" : "um-sortmenu__item"}
                        onClick={() => {
                          setSortBy(o.value);
                          setSortMenuOpen(false);
                        }}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className={drawerOpen ? "um-tablewrap um-tablewrap--draweropen" : "um-tablewrap"}>
            <DataTable
              value={pageUsers}
              dataKey="_id"
              className="um-datatable"
              emptyMessage="No users found."
              rowHover
            >
              <Column header="Administrator" body={userCellTemplate} style={{ width: "32%" }} />
              <Column header="Role" body={roleBadgeTemplate} style={{ width: "14%" }} sortable sortField="user_type" />
              <Column header="Permissions" body={permissionsTemplate} style={{ width: "22%" }} />
              <Column header="Last Login" body={statusTemplate} style={{ width: "20%" }} />
              <Column header="Actions" body={actionBodyTemplate} style={{ width: "12%" }} />
            </DataTable>

            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
              itemLabel="administrators"
            />
          </div>
        </>
      )}

      {actionMenuPortal}

      {drawerOpen ? (
        <button
          type="button"
          className="um-drawer-scrim"
          aria-label="Close drawer"
          onClick={closeDrawer}
        />
      ) : null}

      <aside className={drawerOpen ? "um-drawer open" : "um-drawer"} aria-hidden={!drawerOpen}>
          <div className="um-drawer__header">
            <div className="um-drawer__kicker">Permissions &amp; Role Assignment</div>
            <button type="button" className="um-drawer__close" aria-label="Close" onClick={closeDrawer}>
              <FaTimes />
            </button>
          </div>

          <div className="um-drawer__user">
            <div className="um-drawer__avatar">
              {String(drawerName).slice(0, 1).toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="um-drawer__name">{drawerName}</div>
              <div className="um-drawer__meta">
                {drawerRole ? <span className="um-drawer__role">{drawerRole}</span> : null}
                {drawerEmail ? <span className="um-drawer__email">{drawerEmail}</span> : null}
              </div>
            </div>
          </div>

          <div className="um-drawer__body">
            {PERMISSION_SECTIONS.map((section) => (
              <div key={section.title} className="um-drawer__group">
                <div className="um-drawer__groupTitle">{section.title}</div>
                {section.items.map((item) => (
                  <label key={item.key} className="um-toggle">
                    <span className="um-toggle__text">{item.label}</span>
                    <input
                      className="um-toggle__input"
                      type="checkbox"
                      checked={Boolean(drawerPermissions?.[item.key])}
                      onChange={(e) =>
                        setDrawerPermissions((prev) => ({
                          ...prev,
                          [item.key]: e.target.checked,
                        }))
                      }
                    />
                    <span className="um-switch" />
                  </label>
                ))}
              </div>
            ))}
          </div>

          <div className="um-drawer__footer">
            <button type="button" className="um-drawer__btn um-drawer__btn--ghost" onClick={closeDrawer}>Cancel</button>
            <button
              type="button"
              className="um-drawer__btn um-drawer__btn--primary"
              onClick={handleSaveDrawerPermissions}
              disabled={drawerSaving}
            >
              {drawerSaving ? "Saving…" : "Save Changes"}
            </button>
          </div>
      </aside>

      {addDialogOpen ? (
        <div className="um-modaloverlay" role="dialog" aria-modal="true" aria-label="Create User">
          <button type="button" className="um-modaloverlay__scrim" aria-label="Close add user dialog" onClick={closeAddDialog} />
          <Formik
            initialValues={dialogInitialValues}
            validationSchema={addUserValidationSchema}
            onSubmit={handleSaveUser}
            enableReinitialize
          >
            {({ values, errors, touched, setFieldValue, validateForm, setTouched }) => (
              <Form className="um-adddialog-shell um-adddialog-shell--overlay">
                <div className="um-adddialog">
                  <div className="um-adddialog__header">
                    <div>
                      <h2 className="um-adddialog__title">{addDialogMode === "edit" ? "Edit User" : "Create User"}</h2>
                      <div className="um-adddialog__subtitle">{addDialogMode === "edit" ? "Update administrative access for this user." : "Grant administrative access to your organization."}</div>
                    </div>
                    <button type="button" className="um-adddialog__close" onClick={closeAddDialog}>
                      <FaTimes />
                    </button>
                  </div>

                  <div className="um-adddialog__steps">
                    <button
                      type="button"
                      className={addDialogStep === 1 ? "um-adddialog__step is-active" : "um-adddialog__step"}
                      onClick={() => setAddDialogStep(1)}
                    >
                      <span className="um-adddialog__stepIndex">1</span>
                      <span>Basic Info</span>
                    </button>
                    <button
                      type="button"
                      className={addDialogStep === 2 ? "um-adddialog__step is-active" : "um-adddialog__step"}
                      onClick={() => setAddDialogStep(2)}
                    >
                      <span className="um-adddialog__stepIndex">2</span>
                      <span>Permission Assignment</span>
                    </button>
                  </div>

                  <div className="um-adddialog__body">
                    {addDialogStep === 1 ? (
                      <>
                        <div className="um-adddialog__grid">
                          <div className="um-adddialog__field">
                            <label className="um-adddialog__label" htmlFor="add-user-name">Full Name</label>
                            <Field id="add-user-name" name="name" type="text" className="um-adddialog__input" placeholder="e.g. Alexander Vance" />
                            {touched.name && errors.name ? <div className="error-text">{errors.name}</div> : <div className="error-text" />}
                          </div>

                          <div className="um-adddialog__field">
                            <label className="um-adddialog__label" htmlFor="add-user-department">Department</label>
                            <div className="um-formselectwrap" ref={departmentMenuRef}>
                              <button
                                type="button"
                                id="add-user-department"
                                className={departmentMenuOpen ? "um-sortpill um-formselect open" : "um-sortpill um-formselect"}
                                onClick={() => setDepartmentMenuOpen((open) => !open)}
                                aria-haspopup="menu"
                                aria-expanded={departmentMenuOpen}
                              >
                                <span className="um-formselect__value">{values.department}</span>
                                <FaChevronDown className={departmentMenuOpen ? "um-sortpill__icon open" : "um-sortpill__icon"} />
                              </button>

                              {departmentMenuOpen ? (
                                <div className="um-sortmenu um-formselectmenu" role="menu">
                                  {DEPARTMENT_OPTIONS.map((option) => (
                                    <button
                                      key={option.value}
                                      type="button"
                                      role="menuitem"
                                      className={option.value === values.department ? "um-sortmenu__item active" : "um-sortmenu__item"}
                                      onClick={() => {
                                        setFieldValue("department", option.value);
                                        setDepartmentMenuOpen(false);
                                      }}
                                    >
                                      {option.label}
                                    </button>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                            {touched.department && errors.department ? <div className="error-text">{errors.department}</div> : <div className="error-text" />}
                          </div>
                        </div>

                        <div className="um-adddialog__field um-adddialog__field--full">
                          <label className="um-adddialog__label" htmlFor="add-user-email">Enterprise Email</label>
                          <Field id="add-user-email" name="username" type="email" className="um-adddialog__input" placeholder="vance@omnigate.enterprise" disabled={addDialogMode === "edit"} />
                          {touched.username && errors.username ? <div className="error-text">{errors.username}</div> : <div className="error-text" />}
                        </div>

                        <div className="um-adddialog__field um-adddialog__field--full">
                          <label className="um-adddialog__label">Initial Role</label>
                          <div className="um-rolecards">
                            {ROLE_OPTIONS.map((role) => {
                              const Icon = role.icon;
                              const active = values.roles.includes(role.value);

                              return (
                                <button
                                  key={role.value}
                                  type="button"
                                  className={active ? "um-rolecard active" : "um-rolecard"}
                                  onClick={() => setFieldValue("roles", [role.value])}
                                >
                                  <span className="um-rolecard__icon"><Icon /></span>
                                  <span className="um-rolecard__label">{role.label}</span>
                                </button>
                              );
                            })}
                          </div>
                          {touched.roles && errors.roles ? <div className="error-text">{errors.roles}</div> : <div className="error-text" />}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="um-adddialog__permintro">
                          <div className="um-adddialog__permtitle">Permission Assignment</div>
                          <div className="um-adddialog__permsubtitle">Configure the same permissions available in the side drawer for this administrator.</div>
                        </div>

                        <div className="um-adddialog__permissions">
                          <div className="um-drawer__body um-drawer__body--modal">
                            {PERMISSION_SECTIONS.map((section) => (
                              <div key={section.title} className="um-drawer__group">
                                <div className={section.accentClass}>{section.title}</div>
                                {section.items.map((item) => (
                                  <label key={item.key} className="um-toggle">
                                    <span className="um-toggle__text">{item.label}</span>
                                    <input
                                      className="um-toggle__input"
                                      type="checkbox"
                                      checked={Boolean(values.permissions?.[item.key])}
                                      onChange={(event) => {
                                        setFieldValue(`permissions.${item.key}`, event.target.checked);
                                      }}
                                    />
                                    <span className="um-switch" />
                                  </label>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="um-adddialog__footer">
                    <button type="button" className="um-adddialog__cancel" onClick={closeAddDialog}>Cancel</button>
                    {addDialogStep === 1 ? (
                      <button
                        type="button"
                        className="um-adddialog__submit"
                        onClick={async () => {
                          setTouched({ name: true, username: true, department: true, roles: true }, true);
                          const nextErrors = await validateForm();
                          if (nextErrors.name || nextErrors.username || nextErrors.department || nextErrors.roles) return;
                          setTouched({}, false);
                          setAddDialogStep(2);
                        }}
                      >
                        <span>Next</span>
                        <FaArrowRight />
                      </button>
                    ) : (
                      <div className="um-adddialog__actions">
                        <button type="button" className="um-adddialog__back" onClick={() => setAddDialogStep(1)}>Back</button>
                        <button type="submit" className="um-adddialog__submit" disabled={createLoading}>
                          <span>{createLoading ? "Saving..." : addDialogMode === "edit" ? "Save Changes" : "Create User"}</span>
                          <FaArrowRight />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="um-modaloverlay" role="dialog" aria-modal="true" aria-label="Delete User">
          <button type="button" className="um-modaloverlay__scrim" aria-label="Close delete confirmation" onClick={closeDeleteDialog} />
          <div className="um-confirmdialog">
            <div className="um-confirmdialog__header">
              <h3 className="um-confirmdialog__title">Delete User</h3>
              <button type="button" className="um-adddialog__close" onClick={closeDeleteDialog}>
                <FaTimes />
              </button>
            </div>
            <div className="um-confirmdialog__body">
              <p className="um-confirmdialog__text">
                Are you sure you want to delete <strong>{deleteTarget.name || deleteTarget.username}</strong>?
              </p>
              <p className="um-confirmdialog__subtext">
                This action will remove the user from the administrator list.
              </p>
            </div>
            <div className="um-confirmdialog__footer">
              <button type="button" className="um-adddialog__back" onClick={closeDeleteDialog}>Cancel</button>
              <button
                type="button"
                className="um-confirmdialog__delete"
                onClick={() => {
                  handleDeleteUser(deleteTarget._id);
                  closeDeleteDialog();
                }}
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default UsersList;
